import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import moment from 'moment';
import { rentalService, userService, rentableItemService } from '../../services/api';
import ExportCSVButton from '../Common/ExportCSVButton';
import { exportToCSV } from '../../utils/csvExport';
import '../../styles/common.css';

const RentalList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal per le note
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState('');
  
  // Filtri
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [startDateFrom, setStartDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [startDateTo, setStartDateTo] = useState(searchParams.get('dateTo') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || ''); // '' | 'active' | 'returned'

  // Export state
  const [exporting, setExporting] = useState(false);

  // Sincronizza filtri nell'URL per ripristino al back
  useEffect(() => {
    const params = {};
    if (searchText) params.search = searchText;
    if (statusFilter) params.status = statusFilter;
    if (startDateFrom) params.dateFrom = startDateFrom;
    if (startDateTo) params.dateTo = startDateTo;
    if (page && page !== 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [page, searchText, statusFilter, startDateFrom, startDateTo, setSearchParams]);

  const loadRentals = useCallback(async () => {
    try {
      setLoading(true);
      const filter = {
        searchText: searchText || null,
        userId: selectedUser?.value || null,
        rentableItemId: selectedItem?.value || null,
        startDateFrom: startDateFrom || null,
        startDateTo: startDateTo || null,
        isActive: statusFilter === 'active' ? true : statusFilter === 'returned' ? false : null
      };

      const result = await rentalService.getAll(page, 10, filter);

      if (result.success) {
        setRentals(result.data.items);
        setTotalPages(result.data.totalPages);
        setTotalCount(result.data.totalCount);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel caricamento dei noleggi');
    } finally {
      setLoading(false);
    }
  }, [page, searchText, selectedUser, selectedItem, startDateFrom, startDateTo, statusFilter]);

  useEffect(() => {
    loadRentals();
  }, [loadRentals]);

  // Load users for select
  const loadUsersAsync = useCallback((inputValue) => {
    return new Promise((resolve) => {
      // Se inputValue è vuoto o undefined, usa una stringa vuota
      const searchTerm = inputValue || '';
      userService.getUsers({ search: searchTerm, page: 1, limit: 20 }).then(response => {
        if (response.success) {
          const formattedUsers = (response.data.items || []).map(user => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName} (${user.email})`
          }));
          resolve(formattedUsers);
        } else {
          resolve([]);
        }
      }).catch(() => {
        resolve([]);
      });
    });
  }, []);

  // Load rentable items for select
  const loadItemsAsync = useCallback((inputValue) => {
    return new Promise((resolve) => {
      // Se inputValue è vuoto o undefined, usa una stringa vuota
      const searchTerm = inputValue || '';
      rentableItemService.getAll(1, 20, searchTerm, true).then(response => {
        if (response.success) {
          const formattedItems = (response.data.items || []).map(item => ({
            value: item.id,
            label: item.name
          }));
          resolve(formattedItems);
        } else {
          resolve([]);
        }
      }).catch(() => {
        resolve([]);
      });
    });
  }, []);

  const handleReturn = async (id) => {
    if (window.confirm('Confermi la restituzione di questo noleggio?')) {
      const result = await rentalService.return(id);
      if (result.success) {
        loadRentals();
      } else {
        setError(result.message);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo noleggio? Questa azione non può essere annullata.')) {
      const result = await rentalService.delete(id);
      if (result.success) {
        loadRentals();
      } else {
        setError(result.message);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFilterChange = () => {
    setPage(1); // Reset to first page when filtering
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const isOverdue = (rental) => {
    if (rental.actualReturnDate) return false; // Already returned
    if (!rental.expectedReturnDate) return false; // No expected date
    const now = new Date();
    const expected = new Date(rental.expectedReturnDate);
    return now > expected;
  };

  const handleShowNotes = (notes) => {
    setSelectedNotes(notes);
    setShowNotesModal(true);
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    setExporting(true);
    setError('');
    
    try {
      const filter = {
        searchText: searchText || null,
        userId: selectedUser?.value || null,
        rentableItemId: selectedItem?.value || null,
        startDateFrom: startDateFrom || null,
        startDateTo: startDateTo || null,
        isActive: statusFilter === 'active' ? true : statusFilter === 'returned' ? false : null,
        exportAll: true
      };

      const result = await rentalService.getAll(1, 10, filter);

      if (result.success && result.data.items) {
        const columns = [
          { label: 'Utente', accessor: (r) => `${r.user.firstName} ${r.user.lastName}` },
          { label: 'Email', accessor: (r) => r.user.email },
          { label: 'Articolo', accessor: (r) => r.rentableItem.name },
          { label: 'Quantità', accessor: (r) => r.quantity },
          { label: 'Data Inizio', accessor: (r) => moment(r.startDate).format('DD/MM/YYYY') },
          { label: 'Data Restituzione Prevista', accessor: (r) => moment(r.expectedReturnDate).format('DD/MM/YYYY') },
          { label: 'Data Restituzione Effettiva', accessor: (r) => r.actualReturnDate ? moment(r.actualReturnDate).format('DD/MM/YYYY') : 'Non ancora restituito' },
          { label: 'Prezzo', accessor: (r) => `€${r.rentableItem.price.toFixed(2)}` },
          { label: 'Cauzione', accessor: (r) => `€${r.rentableItem.deposit.toFixed(2)}` },
          { label: 'Note', accessor: (r) => r.notes || '' },
          { label: 'Stato', accessor: (r) => r.actualReturnDate ? 'Restituito' : 'Attivo' }
        ];
        
        exportToCSV(result.data.items, 'noleggi', columns);
      } else {
        setError(result.message || 'Errore durante l\'esportazione');
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Errore durante l\'esportazione dei noleggi');
    } finally {
      setExporting(false);
    }
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedNotes('');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>Gestione Noleggi</h1>
            <div>
              <ExportCSVButton 
                onExport={handleExportCSV}
                loading={exporting}
                disabled={loading}
                className="me-2"
              />
              <button
                className="btn btn-primary"
                onClick={() => navigate('/rentals/new')}
              >
                <i className="fas fa-plus me-2"></i>
                Nuovo Noleggio
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="searchFilter" className="form-label">
                    Ricerca testuale
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="searchFilter"
                    placeholder="Cerca utente, oggetto o note..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      handleFilterChange();
                    }}
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="statusFilter" className="form-label">
                    Stato
                  </label>
                  <select
                    className="form-select"
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      handleFilterChange();
                    }}
                  >
                    <option value="">Tutti</option>
                    <option value="active">Solo attivi</option>
                    <option value="returned">Solo restituiti</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label htmlFor="userFilter" className="form-label">
                    Filtra per utente
                  </label>
                  <AsyncSelect
                    id="userFilter"
                    cacheOptions
                    defaultOptions
                    loadOptions={loadUsersAsync}
                    onChange={(option) => {
                      setSelectedUser(option);
                      handleFilterChange();
                    }}
                    value={selectedUser}
                    placeholder="Seleziona un utente..."
                    noOptionsMessage={() => "Nessun utente trovato"}
                    loadingMessage={() => "Caricamento utenti..."}
                    isClearable
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="itemFilter" className="form-label">
                    Filtra per oggetto
                  </label>
                  <AsyncSelect
                    id="itemFilter"
                    cacheOptions
                    defaultOptions
                    loadOptions={loadItemsAsync}
                    onChange={(option) => {
                      setSelectedItem(option);
                      handleFilterChange();
                    }}
                    value={selectedItem}
                    placeholder="Seleziona un oggetto..."
                    noOptionsMessage={() => "Nessun oggetto trovato"}
                    loadingMessage={() => "Caricamento oggetti..."}
                    isClearable
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="startDateFrom" className="form-label">
                    Data inizio da
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="startDateFrom"
                    value={startDateFrom}
                    onChange={(e) => {
                      setStartDateFrom(e.target.value);
                      handleFilterChange();
                    }}
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="startDateTo" className="form-label">
                    Data inizio a
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="startDateTo"
                    value={startDateTo}
                    onChange={(e) => {
                      setStartDateTo(e.target.value);
                      handleFilterChange();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Rentals Table */}
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                  </div>
                </div>
              ) : rentals.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-clipboard-list fa-3x mb-3"></i>
                  <p>Nessun noleggio trovato</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Utente</th>
                          <th>Oggetto</th>
                          <th>Quantità</th>
                          <th>Data Inizio</th>
                          <th>Restituzione Prevista</th>
                          <th>Restituzione Effettiva</th>
                          <th>Stato</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rentals.map((rental) => (
                          <tr key={rental.id} className={isOverdue(rental) ? 'table-warning' : ''}>
                            <td>
                              <strong 
                              className="text-primary text-decoration-none cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/users/${rental.user.id}/edit`)}
                              title="Clicca per visualizzare la scheda atleta"
                            >
                              {rental.user.firstName} {rental.user.lastName}
                            </strong>
                              <br />
                              <small className="text-muted">{rental.user.email}</small>
                            </td>
                            <td>
                              <strong>{rental.rentableItem.name}</strong>
                              <br />
                              <small className="text-muted">
                                {formatCurrency(rental.rentableItem.price)}
                              </small>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{rental.quantity}</span>
                            </td>
                            <td>{formatDate(rental.startDate)}</td>
                            <td>{formatDate(rental.expectedReturnDate)}</td>
                            <td>
                              {rental.actualReturnDate ? (
                                <span className="text-success">
                                  {formatDate(rental.actualReturnDate)}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {rental.actualReturnDate ? (
                                  <span className="badge bg-success">Restituito</span>
                                ) : isOverdue(rental) ? (
                                  <span className="badge bg-warning text-dark">In ritardo</span>
                                ) : (
                                  <span className="badge bg-primary">Attivo</span>
                                )}
                                {rental.notes && (
                                  <i 
                                    className="fas fa-info-circle text-info" 
                                    style={{ cursor: 'pointer' }}
                                    title={rental.notes.length > 32 ? rental.notes.substring(0, 32) + '...' : rental.notes}
                                    onClick={() => handleShowNotes(rental.notes)}
                                  ></i>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="btn-group" role="group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  title="Modifica"
                                  onClick={() => navigate(`/rentals/${rental.id}/edit`)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                {!rental.actualReturnDate && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    title="Segna come restituito"
                                    onClick={() => handleReturn(rental.id)}
                                  >
                                    <i className="fas fa-check"></i>
                                  </button>
                                )}
                                {rental.actualReturnDate && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    title="Elimina"
                                    onClick={() => handleDelete(rental.id)}
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label="Navigazione pagine">
                      <ul className="pagination justify-content-center mt-4">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                          >
                            Precedente
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li
                            key={i + 1}
                            className={`page-item ${page === i + 1 ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(i + 1)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${page === totalPages ? 'disabled' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                          >
                            Successiva
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}

                  <div className="text-muted text-center mt-3">
                    Totale: {totalCount} noleggi
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal per visualizzare le note */}
      {showNotesModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCloseNotesModal}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-sticky-note me-2"></i>
                  Note del noleggio
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseNotesModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedNotes}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseNotesModal}
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalList;
